import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckIcon, CloseIcon, InfoIcon, WarningIcon } from './icons';

const ToastContext = createContext(null);

const DURATION = { success: 5000, info: 6000, error: 10000 };
const ICONS = { success: CheckIcon, info: InfoIcon, error: WarningIcon };
let nextId = 1;

function ToastItem({ toast, onDismiss }) {
  const { t } = useTranslation();
  const [paused, setPaused] = useState(false);
  const remaining = useRef(toast.duration);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!toast.duration || paused) return undefined;
    startedAt.current = Date.now();
    const timer = setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [paused, toast.id, toast.duration, onDismiss]);

  const Icon = ICONS[toast.type] || InfoIcon;
  return (
    <li
      className={`ui-toast ui-toast--${toast.type}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="ui-toast__icon"><Icon size={18} /></span>
      <div className="ui-toast__body">
        {toast.title && <p className="ui-toast__title">{toast.title}</p>}
        <p className="ui-toast__message">{toast.message}</p>
      </div>
      {toast.action && (
        <button
          type="button"
          className="ui-toast__action"
          onClick={() => { toast.action.onClick(); onDismiss(toast.id); }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="ui-toast__close" aria-label={t('ui.dismiss')} onClick={() => onDismiss(toast.id)}>
        <CloseIcon size={16} />
      </button>
    </li>
  );
}

/**
 * Provides useToast(): toast.success(message, options), toast.error(…),
 * toast.info(…). Options: {title, action: {label, onClick}, duration (ms, 0 =
 * stays)}. Messages are read out by an aria-live="polite" region, errors by an
 * assertive one (role="alert"), and stay longer. Hovering or focusing a toast pauses it.
 */
export function ToastProvider({ children }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((item) => item.id !== id)), []);

  const show = useCallback((type, message, options = {}) => {
    const id = nextId++;
    const duration = options.duration ?? DURATION[type];
    setToasts((list) => [...list.slice(-3), { id, type, message, title: options.title, action: options.action, duration }]);
    return id;
  }, []);

  const api = useMemo(() => ({
    success: (message, options) => show('success', message, options),
    error: (message, options) => show('error', message, options),
    info: (message, options) => show('info', message, options),
    dismiss,
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <section className="ui-toasts" aria-label={t('ui.notifications')}>
          <ol aria-live="polite" aria-relevant="additions text" className="ui-toasts__list">
            {toasts.filter((toast) => toast.type !== 'error').map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
          </ol>
          <ol role="alert" aria-live="assertive" aria-relevant="additions text" className="ui-toasts__list">
            {toasts.filter((toast) => toast.type === 'error').map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
          </ol>
        </section>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast needs a <ToastProvider> (see ui/UIProvider).');
  return toast;
}
